import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';
import TideForms from '../marinetools/components/tides/TideForms.jsx';
import tidesUnderKeelClearance from '../data/tidesUnderKeelClearance.js';

export default function TidesUnderKeelClearance() {
  return (
    <section className="as-section">
      <div className="container">
        <h1 className="as-page-title">Tides &ndash; Under-Keel Clearance</h1>
        <p className="as-lead">
          Under-Keel Clearance (UKC) is the vital vertical distance between a
          ship&rsquo;s lowest hull point (keel) and the seabed, ensuring enough water to
          prevent grounding. UKC is calculated by subtracting the ship&rsquo;s draft from
          the available water depth as well as factoring in dynamic effects like tides
          and waves for safe passage, especially in shallow waters. The UKC height could
          be defined in your vessel&rsquo;s standard operating procedure or decided by the
          master depending on the sea conditions on the day.
        </p>
        <p>
          Insufficient UKC risks grounding, which can cause vessel damage, cargo loss, and
          danger to crew. Mariners must calculate UKC carefully before entering shallow
          waters or crossing bars.
        </p>

        <h3 className="as-subheading-sm">Requirement Definitions</h3>
        <ul>
          <li><strong>Required Depth</strong> &ndash; Depth between Sea Level and Sea Bed</li>
          <li><strong>Draft</strong> &ndash; Height of Hull below water line</li>
          <li><strong>UKC</strong> &ndash; Clearance between bottom of keel and seabed</li>
          <li><strong>Charted Depth</strong> &ndash; Depth of water at LAT.</li>
          <li><strong>Tide</strong> &ndash; Tidal Height above LAT</li>
        </ul>

        <h2 className="as-subheading">UKC Example Calculations</h2>
        <p>Following is the formula for calculating required height of tide:</p>
        <ul>
          <li>Required UKC + Vessel Draft = Required depth of water</li>
          <li>Required depth of water &minus; Charted Depth = Required Height of Tide</li>
        </ul>
        <p>
          In this example, a vessel with a draft of 2.5 metres requires safe under-keel
          clearance of 1.5 metres over the sand bar marked on the chart as 3 metres at
          LAT. What tide is required to safely pass into the inlet?
        </p>
        <Table bordered responsive className="as-data-table">
          <tbody>
            <tr><td>UKC Required</td><td>1.5m</td></tr>
            <tr><td>Vessel Draft</td><td>2.5m</td></tr>
            <tr><td>Required Depth of water (Draft + UKC)</td><td>4m</td></tr>
            <tr><td>Charted Depth</td><td>3m</td></tr>
            <tr><td>Required Height of Tide (Required &minus; Charted)</td><td>1m</td></tr>
          </tbody>
        </Table>

        <p>
          To calculate at what time it is safe to pass over the bar, you will again need
          to use the Standard Tide Curves finding the required depth. Using an example,
          let&rsquo;s say that we estimate to arrive outside the Brisbane Bar at 18:00 and
          need to work out when we can safely pass over. We know we require a 1m tide to
          have a minimum required depth of 4m. We now need to find the tide in the tides
          table, calculate tide range and plot on the Tidal Curve to find the time
          difference to high water.
        </p>
        <p>Following is an example extract of Tide Levels from the 2025 Queensland Tide Tables on 16th of April 2025 for Brisbane Bar:</p>
        <Table bordered responsive className="as-data-table">
          <thead><tr><th>Description</th><th>Time</th><th>Metres from (LAT)</th></tr></thead>
          <tbody>
            <tr><td>Low Water (LW)</td><td>05:32</td><td>0.73m</td></tr>
            <tr><td>High Water (HW)</td><td>11:04</td><td>1.88m</td></tr>
            <tr><td>Low Water (LW)</td><td>17:12</td><td>0.56m</td></tr>
            <tr><td>High Water (HW)</td><td>23:41</td><td>2.33m</td></tr>
          </tbody>
        </Table>
        <Table bordered responsive className="as-data-table">
          <tbody>
            <tr><td>High Water</td><td>2.33m</td></tr>
            <tr><td>Low Water</td><td>0.56m</td></tr>
            <tr><td>Tidal Range</td><td>1.77m</td></tr>
          </tbody>
        </Table>

        <p>
          Having calculated tidal range, we can plot the height on the Tidal Curve to
          find time where we have required depth. Please note that the projected tidal
          range (green line) is a line that is offset from the nearest existing range
          curve (black curve lines):
        </p>
        <ul>
          <li>Find Height: Required Height &minus; Low Water = 1m &minus; 0.56m = 0.44m</li>
          <li>Plot a horizontal line from 0.44m on the left vertical axis of the Tidal Chart (blue line) through intersection projected 1.77m tidal range (green line on tidal chart)</li>
          <li>Plot a vertical line intersection horizontal line (red line) from projected 1.77m tidal range and intersected horizontal line</li>
          <li>The plotted hourly interval as read off the x-axis is: 4:10</li>
          <li>Required depth will be at: High Water Time &minus; Tidal Curve Interval = 23:41 &minus; 4:10 = 19:31</li>
        </ul>
        <p>At 19:31, the tide height is approximately 1 meter above the Lowest Astronomical Tide (LAT) and safe to pass over the bar.</p>

        <h2 className="as-subheading">Progress Check</h2>
        <p>
          Complete the exercises in each tab to check your progress for Under-Keel
          Clearance. Please use the Queensland MSQ 2025 Tide Tables as the exclusive
          reference in all the exercises. Any tide times should be formatted as HH:mm
          (Example: 09:35). This would typically show as 0935 in the Queensland Tide
          tables.
        </p>
        <p className="fst-italic">Please note that charted depth used in examples may be fictional and for the purpose of the exercise only.</p>

        <TideForms data={tidesUnderKeelClearance} />

        <div className="d-flex justify-content-between mt-4">
          <Link to="/tides-secondary-ports" className="btn-as-outline">&larr; Previous &ndash; Secondary Ports</Link>
        </div>
      </div>
    </section>
  );
}
